import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { formatCurrency } from '../../shared/utils/format';
import {
  StatCard,
  StatsGrid,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Button,
  LoadingSkeleton,
} from '@nexora/design-system';

/* ── Types ── */

interface DashboardSummary {
  totalPayables: number;
  totalReceivables: number;
  cashBalance: number;
  overduePayables: number;
  overdueReceivables: number;
  shortTermPayables: number;
  shortTermReceivables: number;
  netShortTermPosition: number;
  overduePayablesCount: number;
  overdueReceivablesCount: number;
}

type Period = 'monthly' | 'quarterly';

interface SeriesPoint {
  label: string;
  vendas: number;
  meta: number;
}

/* ── Helpers ── */

/** Uma parcela da projecao de fluxo de caixa, como o backend a devolve. */
interface CashFlowEntry {
  period: string;
  periodStart: string;
  periodEnd: string;
  projectedInflows: number;
  projectedOutflows: number;
  netCashFlow: number;
  closingBalance: number;
}

interface CashFlowResponse {
  entries: CashFlowEntry[];
  totalInflows: number;
  totalOutflows: number;
  netProjected: number;
}

/** `GET /finance/dashboards/dre` — usado so para a margem do periodo. */
interface DreEntry {
  label: string;
  value: number;
  level: number;
  isSubtotal: boolean;
}

interface DreResponse {
  periodStart: string;
  periodEnd: string;
  entries: DreEntry[];
  netResult: number;
}

/** Margem do periodo: resultado liquido sobre a receita bruta da DRE. */
function margemDoPeriodo(dre: DreResponse | undefined): number | null {
  if (!dre || !Array.isArray(dre.entries)) return null;
  const receita = dre.entries.find((e) => /receita bruta/i.test(e.label))?.value;
  if (typeof receita !== 'number' || receita === 0) return null;
  return (dre.netResult / receita) * 100;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function inicioDoMes(): string {
  const hoje = new Date();
  return isoDate(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
}

type RiskLevel = 'Baixo' | 'Médio' | 'Alto';

function assessRisk(data: DashboardSummary): { level: RiskLevel; pending: number } {
  /* `?? 0` por campo: contagem ausente na resposta significa "nada vencido
     reportado", e a soma direta de um `undefined` tipado como number
     produzia NaN — que chegou a aparecer como "NaN itens vencidos" no
     Painel Executivo renderizado. */
  const pending = (data.overduePayablesCount ?? 0) + (data.overdueReceivablesCount ?? 0);
  const overdueValue = data.overduePayables + data.overdueReceivables;
  if (pending === 0 && overdueValue === 0) return { level: 'Baixo', pending };
  if (pending > 5 || (data.cashBalance >= 0 && overdueValue > data.cashBalance)) {
    return { level: 'Alto', pending };
  }
  return { level: 'Médio', pending };
}

function healthScore(data: DashboardSummary): number {
  let score = 8;
  if (data.netShortTermPosition < 0) score -= 2;
  /* `?? 0` por campo: contagem ausente na resposta significa "nada vencido
     reportado", e a soma direta de um `undefined` tipado como number
     produzia NaN — que chegou a aparecer como "NaN itens vencidos" no
     Painel Executivo renderizado. */
  const pending = (data.overduePayablesCount ?? 0) + (data.overdueReceivablesCount ?? 0);
  if (pending > 5) score -= 2;
  else if (pending > 0) score -= 1;
  if (data.cashBalance < 0) score -= 2;
  return Math.max(0, Math.min(10, score));
}

/* ── Icons (stroke, 22px) ── */

function iconProps() {
  return {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const;
}

function CashIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  );
}

function SalesIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  );
}

function ScaleIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <path d="M12 3v18" />
      <path d="M5 7h14" />
      <path d="M5 7l-3 7a3 3 0 0 0 6 0L5 7Z" />
      <path d="M19 7l-3 7a3 3 0 0 0 6 0l-3-7Z" />
    </svg>
  );
}

function AlertIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/* ── SVG chart ── */

function ComparativeBarsChart({ points }: { points: SeriesPoint[] }): JSX.Element {
  const W = 640;
  const H = 260;
  /* Espaco a esquerda para o rotulo da linha de grade: na borda direita ele
     ficava ATRAS das barras dos ultimos periodos (mesmo defeito do grafico do
     Painel Financeiro — todo24 T51). */
  const padL = 96;
  const padR = 12;
  const padB = 28;
  const padT = 28;
  const max = Math.max(1, ...points.map((p) => Math.max(p.vendas, p.meta)));
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const slot = innerW / Math.max(1, points.length);
  const barW = Math.min(26, Math.max(10, slot / 4));
  const gridLines = [0.25, 0.5, 0.75, 1];

  return (
    <div className="exec-chart-scroll">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="exec-chart"
        role="img"
        aria-label={`Entradas e saídas previstas em ${points.length} períodos. Máximo de ${formatCurrency(max)}.`}
      >
        {gridLines.map((f) => {
          const y = padT + innerH * (1 - f);
          return (
            <g key={f}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} className="exec-grid" />
              <text x={padL - 6} y={y - 4} textAnchor="end" className="exec-grid-label">
                {formatCurrency(max * f)}
              </text>
            </g>
          );
        })}
        {points.map((p, i) => {
          const cx = padL + slot * i + slot / 2;
          const hv = (p.vendas / max) * innerH;
          const hm = (p.meta / max) * innerH;
          const yv = padT + innerH - hv;
          const ym = padT + innerH - hm;
          return (
            <g key={p.label}>
              {/* Primeira serie a esquerda, na ordem da legenda: a barra
                  escura vinha depois da laranja e a leitura invertia. */}
              <title>{`${p.label}: entradas ${formatCurrency(p.vendas)} · saídas ${formatCurrency(p.meta)}`}</title>
              <rect
                x={cx + 3}
                y={ym}
                width={barW}
                height={Math.max(2, hm)}
                rx={4}
                className="exec-bar-meta"
              />
              <rect
                x={cx - barW - 3}
                y={yv}
                width={barW}
                height={Math.max(2, hv)}
                rx={4}
                className="exec-bar-vendas"
              />
              <text x={cx} y={H - 8} textAnchor="middle" className="exec-x-label">
                {p.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function HealthDonut({ score }: { score: number }): JSX.Element {
  const R = 34;
  const C = 2 * Math.PI * R;
  const filled = (score / 10) * C;
  return (
    <svg
      viewBox="0 0 96 96"
      width={104}
      height={104}
      role="img"
      aria-label={`Indicador de saúde da operação: ${score} de 10`}
    >
      <circle cx={48} cy={48} r={R} className="donut-track" />
      <circle
        cx={48}
        cy={48}
        r={R}
        className="donut-fill"
        strokeDasharray={`${filled} ${C}`}
        transform="rotate(-90 48 48)"
      />
      <text x={48} y={46} textAnchor="middle" className="donut-score">
        {score}/10
      </text>
      <text x={48} y={62} textAnchor="middle" className="donut-caption">
        saúde
      </text>
    </svg>
  );
}

/* ── Page ── */

export function DashboardPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('monthly');

  const { data, isLoading, error, refetch } = useQuery<DashboardSummary>({
    queryKey: queryKeys.finance.dashboard(tenantId),
    queryFn: async () => apiClient.get<DashboardSummary>(`/finance/dashboards/summary`),
    enabled: !!tenantId,
  });

  /* A janela do grafico. Ao contrario do toggle antigo, cada opcao e uma
     consulta diferente ao servidor — nao um reagrupamento da mesma base. */
  const janela = useMemo(() => {
    const hoje = new Date();
    const fim = new Date(hoje);
    fim.setDate(fim.getDate() + (period === 'monthly' ? 30 : 90));
    return { inicio: isoDate(hoje), fim: isoDate(fim) };
  }, [period]);

  const cashFlowQ = useQuery<CashFlowResponse>({
    queryKey: [...queryKeys.finance.dashboard(tenantId), 'cash-flow', period],
    queryFn: async () =>
      apiClient.get<CashFlowResponse>('/finance/dashboards/cash-flow', {
        params: { periodStart: janela.inicio, periodEnd: janela.fim },
      }),
    enabled: !!tenantId,
  });

  /* A margem do periodo vem da DRE do mes — e o unico lugar onde receita e
     resultado convivem. A referencia mostra "Margem Media ... vs meta"; meta
     nao existe em endpoint nenhum, entao so a margem entra. */
  const dreQ = useQuery<DreResponse>({
    queryKey: [...queryKeys.finance.dashboard(tenantId), 'dre-mes'],
    queryFn: async () =>
      apiClient.get<DreResponse>('/finance/dashboards/dre', {
        params: { periodStart: inicioDoMes(), periodEnd: isoDate(new Date()) },
      }),
    enabled: !!tenantId,
    retry: false,
  });

  const flowEntries = useMemo(
    /* `data?.entries ?? []` nao protege: se a resposta vier como array,
       `[].entries` e o metodo nativo e passa adiante (todo24 T51). */
    () => (Array.isArray(cashFlowQ.data?.entries) ? cashFlowQ.data.entries : []),
    [cashFlowQ.data],
  );
  const margem = useMemo(() => margemDoPeriodo(dreQ.data), [dreQ.data]);
  const risk = useMemo(() => (data ? assessRisk(data) : null), [data]);
  const score = useMemo(() => (data ? healthScore(data) : 0), [data]);

  if (isLoading) {
    return (
      <div className="dashboard-page" aria-busy="true" aria-live="polite">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Visão Geral</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Acompanhamento consolidado da operação</p>
          </div>
        </div>
        <StatsGrid>
          <LoadingSkeleton width="100%" height="120px" />
          <LoadingSkeleton width="100%" height="120px" />
          <LoadingSkeleton width="100%" height="120px" />
          <LoadingSkeleton width="100%" height="120px" />
        </StatsGrid>
        <div className="exec-grid-2">
          <LoadingSkeleton width="100%" height="300px" />
          <LoadingSkeleton width="100%" height="300px" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Visão Geral</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">{tenant?.name}</p>
          </div>
        </div>
        <Card variant="elevated" padding="2rem">
          <div className="error-state" role="alert">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ba1a1a" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <p>Erro ao carregar dados do painel.</p>
            <p className="error-detail">{(error as Error).message}</p>
            <Button
              variant="secondary"
              onClick={() => {
                void refetch();
              }}
            >
              Tentar novamente
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="dashboard-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Visão Geral</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">{tenant?.name}</p>
          </div>
        </div>
        <Card variant="elevated" padding="2rem">
          <div className="empty-state" role="status">
            <p>Nenhum dado disponível para exibir.</p>
            <p className="empty-detail">Conclua o onboarding ou lance os primeiros títulos.</p>
          </div>
        </Card>
      </div>
    );
  }

  const activities = [
    {
      id: 'overdue-pay',
      icon: <AlertIcon />,
      title: `${data.overduePayablesCount ?? 0} títulos a pagar vencidos`,
      detail: `${formatCurrency(data.overduePayables)} em atraso · Financeiro`,
      tone: data.overduePayablesCount > 0 ? 'error' : 'success',
      path: '/finance/payables',
    },
    {
      id: 'overdue-rec',
      icon: <SalesIcon />,
      title: `${data.overdueReceivablesCount ?? 0} títulos a receber vencidos`,
      detail: `${formatCurrency(data.overdueReceivables)} em atraso · Cobrança`,
      tone: data.overdueReceivablesCount > 0 ? 'warning' : 'success',
      path: '/finance/receivables',
    },
    {
      id: 'position',
      icon: <ScaleIcon />,
      title: `Posição líquida (30 dias): ${formatCurrency(data.netShortTermPosition)}`,
      detail:
        data.netShortTermPosition >= 0
          ? 'Cobertura positiva no curto prazo'
          : 'Atenção: cobertura negativa no curto prazo',
      tone: data.netShortTermPosition >= 0 ? 'success' : 'error',
      path: '/finance',
    },
    {
      id: 'cash',
      icon: <CashIcon />,
      title: `Saldo disponível: ${formatCurrency(data.cashBalance)}`,
      detail: `A receber ${formatCurrency(data.shortTermReceivables)} · A pagar ${formatCurrency(data.shortTermPayables)}`,
      tone: 'info',
      path: '/accounting/statements',
    },
  ] as const;

  const riskVariant = risk?.level === 'Alto' ? 'error' : risk?.level === 'Médio' ? 'warning' : 'success';

  return (
    <div className="dashboard-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="eyebrow">Nexora · Business OS</p>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Visão Geral</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            {tenant?.name ?? 'Operação'} · Acompanhamento consolidado da operação
          </p>
        </div>
        <div
          className="period-toggle flex flex-wrap items-center gap-sm"
          role="group"
          aria-label="Janela da projeção de caixa"
        >
          <button
            type="button"
            className={period === 'monthly' ? 'active' : ''}
            aria-pressed={period === 'monthly'}
            onClick={() => setPeriod('monthly')}
          >
            30 dias
          </button>
          <button
            type="button"
            className={period === 'quarterly' ? 'active' : ''}
            aria-pressed={period === 'quarterly'}
            onClick={() => setPeriod('quarterly')}
          >
            90 dias
          </button>
        </div>
      </div>

      <StatsGrid>
        <StatCard
          variant="primary"
          icon={<CashIcon />}
          value={formatCurrency(data.cashBalance)}
          label="Fluxo de caixa · saldo"
          trend={{
            value: `A receber: ${formatCurrency(data.shortTermReceivables)}`,
            direction: 'up',
          }}
        />
        {/* A referência pede "Margem Média ... vs meta". A margem sai da DRE
            do mês (resultado líquido ÷ receita bruta); meta não existe em
            endpoint nenhum, então a comparação não entra. */}
        <StatCard
          variant="success"
          icon={<SalesIcon />}
          value={margem === null ? '—' : `${margem.toFixed(1)}%`}
          label="Margem do mês"
          trend={{
            value:
              margem === null
                ? 'DRE do mês indisponível'
                : `Resultado ${formatCurrency(dreQ.data?.netResult ?? 0)}`,
            direction: (margem ?? 0) >= 0 ? 'up' : 'down',
          }}
        />
        <StatCard
          variant="info"
          icon={<ScaleIcon />}
          value={formatCurrency(data.netShortTermPosition)}
          label="Posição líquida (30 dias)"
          trend={{
            value: data.netShortTermPosition >= 0 ? 'Cobertura positiva' : 'Cobertura negativa',
            direction: data.netShortTermPosition >= 0 ? 'up' : 'down',
          }}
        />
        <StatCard
          variant="warning"
          icon={<AlertIcon />}
          value={risk ? `${risk.level}` : '—'}
          label={`Risco operacional · ${risk?.pending ?? 0} alertas`}
          trend={{
            value: `${(data.overduePayablesCount ?? 0) + (data.overdueReceivablesCount ?? 0)} itens vencidos`,
            direction: (risk?.pending ?? 0) > 0 ? 'down' : 'up',
          }}
        />
      </StatsGrid>

      <div className="exec-grid-2">
        <Card variant="elevated" padding="1.25rem">
          <CardHeader
            action={
              <Badge variant="info" size="sm">
                {period === 'monthly' ? '30 dias' : '90 dias'}
              </Badge>
            }
          >
            <h3 className="exec-title">Fluxo de caixa projetado</h3>
            <p className="exec-subtitle">Entradas e saídas previstas para a janela escolhida</p>
          </CardHeader>
          <CardBody>
            <div className="exec-legend" aria-hidden="true">
              <span className="legend-item">
                <i className="dot dot-vendas" /> Entradas previstas
              </span>
              <span className="legend-item">
                <i className="dot dot-meta" /> Saídas previstas
              </span>
            </div>
            {cashFlowQ.isLoading ? (
              <LoadingSkeleton width="100%" height="220px" />
            ) : flowEntries.length === 0 ? (
              <p className="empty-detail" role="status">
                Sem títulos previstos nesta janela.
              </p>
            ) : (
              <div aria-live="polite">
                <ComparativeBarsChart
                  points={flowEntries.map((e) => ({
                    label: e.period,
                    vendas: Math.round(e.projectedInflows),
                    meta: Math.round(e.projectedOutflows),
                  }))}
                />
              </div>
            )}
            <p className="exec-note">
              Líquido projetado: {formatCurrency(cashFlowQ.data?.netProjected ?? 0)}
            </p>
          </CardBody>
        </Card>

        <div className="exec-side">
          <Card variant="elevated" padding="1.25rem">
            <CardHeader
              action={
                <Badge variant={riskVariant} size="sm" dot>
                  {risk?.level ?? '—'}
                </Badge>
              }
            >
              <h3 className="exec-title">Risco operacional</h3>
              <p className="exec-subtitle">Derivado de títulos vencidos e cobertura de caixa</p>
            </CardHeader>
            <CardBody>
              <div className="alert-list">
                <div className="alert-item">
                  <Badge variant="warning" size="md" dot>
                    {formatCurrency(data.overduePayables)}
                  </Badge>
                  <span>A pagar vencidos ({data.overduePayablesCount} itens)</span>
                </div>
                <div className="alert-item">
                  <Badge variant="error" size="md" dot>
                    {formatCurrency(data.overdueReceivables)}
                  </Badge>
                  <span>A receber vencidos ({data.overdueReceivablesCount} itens)</span>
                </div>
              </div>
              <div className="quick-actions">
                <Button
                  variant="secondary"
                  onClick={() => {
                    void navigate({ to: '/finance/payables' });
                  }}
                >
                  Ver cobranças
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    void navigate({ to: '/finance/reconciliation' });
                  }}
                >
                  Conciliar
                </Button>
              </div>
            </CardBody>
          </Card>

          <Card variant="elevated" padding="1.25rem">
            <CardHeader
              action={
                <Badge variant={score >= 7 ? 'success' : score >= 4 ? 'warning' : 'error'} size="sm">
                  {score >= 7 ? 'Saudável' : score >= 4 ? 'Atenção' : 'Crítica'}
                </Badge>
              }
            >
              <h3 className="exec-title">Indicador principal</h3>
              {/* A referencia mostra "8/10 Empresas com operacao saudavel" — uma
                  contagem de empresas. Aqui o numero e um indice 0-10 da saude
                  financeira DESTA operacao; o rotulo tem de dizer isso. */}
              <p className="exec-subtitle">Índice de saúde financeira da operação</p>
            </CardHeader>
            <CardBody>
              <div className="health-row">
                <HealthDonut score={score} />
                <div>
                  <p className="health-text">
                    Operação {score >= 7 ? 'saudável' : score >= 4 ? 'em atenção' : 'em estado crítico'}{' '}
                    neste período.
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => {
                      void navigate({ to: '/accounting/statements' });
                    }}
                  >
                    Ver detalhamento
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <div className="exec-grid-2">
        <Card variant="elevated" padding="1.25rem">
          <CardHeader>
            <h3 className="exec-title">Atividades recentes</h3>
            <p className="exec-subtitle">O que a posição atual da operação exige de atenção</p>
          </CardHeader>
          <CardBody>
            <ul className="activity-list">
              {activities.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    className="activity-item"
                    onClick={() => {
                      void navigate({ to: a.path });
                    }}
                  >
                    <span className={`activity-icon tone-${a.tone}`}>{a.icon}</span>
                    <span className="activity-text">
                      <strong>{a.title}</strong>
                      <small>{a.detail}</small>
                    </span>
                    <span className="activity-go" aria-hidden="true">
                      →
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card variant="elevated" padding="1.25rem">
          <CardHeader>
            <h3 className="exec-title">Novo registro</h3>
            <p className="exec-subtitle">Atalhos para os fluxos principais</p>
          </CardHeader>
          <CardBody>
            <div className="quick-grid">
              <Button
                variant="secondary"
                onClick={() => {
                  void navigate({ to: '/sales/new' });
                }}
              >
                Vendas · novo pedido
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  void navigate({ to: '/pos/sale' });
                }}
              >
                PDV · frente de caixa
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  void navigate({ to: '/inventory/stock' });
                }}
              >
                Estoque · saldo
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  void navigate({ to: '/fiscal/invoices/new' });
                }}
              >
                Fiscal · emitir NF-e
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      <style>{`
        /* Paleta e tipografia do design system "Core Enterprise Logic"
           (base-html/core_enterprise_logic_2/DESIGN.md). Antes este bloco usava
           a paleta "Obsidian Aurora" (#191c1e / #1a365d / #ed6c02), que nao
           corresponde as 128 telas de referencia. */
        .dashboard-page { padding: 24px; display: flex; flex-direction: column; gap: 24px; }
        .eyebrow { margin: 0 0 4px; font-size: 10px; line-height: 14px; letter-spacing: 0.03em; text-transform: uppercase; color: #43474e; font-weight: 500; font-family: "JetBrains Mono", ui-monospace, monospace; }
        .period-toggle { display: inline-flex; background: #f2f4f6; border: 1px solid #c4c6cf; border-radius: 0.25rem; padding: 2px; gap: 2px; }
        .period-toggle button { border: 0; background: transparent; padding: 6px 14px; border-radius: 0.125rem; font-size: 12px; line-height: 16px; letter-spacing: 0.02em; font-weight: 500; color: #43474e; cursor: pointer; }
        .period-toggle button.active { background: #002045; color: #ffffff; }
        .period-toggle button:focus-visible { outline: 2px solid #002045; outline-offset: 2px; }
        .exec-grid-2 { display: grid; grid-template-columns: 1.4fr 1fr; gap: 24px; align-items: start; }
        .exec-side { display: flex; flex-direction: column; gap: 24px; }
        .exec-title { margin: 0; font-size: 20px; line-height: 28px; font-weight: 600; color: #002045; }
        .exec-subtitle { margin: 2px 0 0; font-size: 12px; line-height: 16px; color: #43474e; }
        .exec-legend { display: flex; gap: 16px; margin: 8px 0; font-size: 12px; line-height: 16px; color: #43474e; flex-wrap: wrap; }
        .legend-item { display: inline-flex; align-items: center; gap: 6px; }
        .dot { width: 10px; height: 10px; border-radius: 9999px; display: inline-block; }
        .dot-vendas { background: #002045; }
        .dot-meta { background: #ed6c02; }
        .exec-chart-scroll { overflow-x: auto; }
        .exec-chart { width: 100%; min-width: 480px; height: auto; display: block; }
        .exec-grid { stroke: #c4c6cf; stroke-width: 1; }
        .exec-grid-label { font-size: 10px; fill: #74777f; font-family: "JetBrains Mono", ui-monospace, monospace; }
        .exec-x-label { font-size: 12px; fill: #43474e; text-transform: capitalize; font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500; letter-spacing: 0.02em;}
        .exec-bar-vendas { fill: #002045; }
        .exec-bar-meta { fill: #ed6c02; opacity: 0.85; }
        .exec-note { font-size: 12px; line-height: 16px; color: #43474e; margin: 8px 0 0; }
        .alert-list { display: flex; flex-direction: column; gap: 8px; }
        .alert-item { display: flex; align-items: center; gap: 8px; font-size: 14px; line-height: 20px; color: #191c1e; }
        .quick-actions { display: flex; gap: 8px; margin-top: 16px; flex-wrap: wrap; }
        .health-row { display: flex; gap: 16px; align-items: center; }
        .health-text { margin: 0 0 8px; color: #191c1e; font-size: 14px; line-height: 20px; }
        .donut-track { fill: none; stroke: #e0e3e5; stroke-width: 12; }
        .donut-fill { fill: none; stroke: #002045; stroke-width: 12; stroke-linecap: round; }
        .donut-score { font-size: 15px; font-weight: 700; fill: #002045; font-family: "JetBrains Mono", ui-monospace, monospace; }
        .donut-caption { font-size: 10px; fill: #43474e; }
        .activity-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
        .activity-item { width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px; background: none; border: 0; border-bottom: 1px solid #c4c6cf; cursor: pointer; text-align: left; border-radius: 0.25rem; }
        .activity-item:hover { background: #eceef0; }
        .activity-item:focus-visible { outline: 2px solid #002045; outline-offset: 2px; }
        .activity-icon { width: 36px; height: 36px; border-radius: 0.25rem; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .tone-error { background: #ffdad6; color: #93000a; }
        .tone-warning { background: #ffddba; color: #633f0f; }
        .tone-success { background: #d3f5d6; color: #0b3d11; }
        .tone-info { background: #d6e3ff; color: #001b3c; }
        .activity-text { display: flex; flex-direction: column; gap: 2px; flex: 1; }
        .activity-text strong { font-size: 14px; line-height: 20px; color: #191c1e; font-weight: 600; }
        .activity-text small { font-size: 12px; line-height: 16px; color: #43474e; }
        .activity-go { color: #74777f; font-size: 18px; }
        .quick-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .error-state { text-align: center; color: #ba1a1a; display: flex; flex-direction: column; align-items: center; gap: 8px; }
        .error-state p { margin: 0; }
        .error-detail { font-size: 12px; line-height: 16px; color: #43474e; }
        .empty-state { text-align: center; color: #43474e; padding: 16px 0; }
        .empty-detail { font-size: 14px; line-height: 20px; }
        @media (max-width: 1080px) { .exec-grid-2 { grid-template-columns: 1fr; } }
        @media (max-width: 640px) {
          .dashboard-page { padding: 16px; }
          .quick-grid { grid-template-columns: 1fr; }
          .health-row { flex-direction: column; align-items: flex-start; }
        }
        @media (prefers-reduced-motion: reduce) {
          .activity-item, .period-toggle button { transition: none; }
        }
      `}</style>
    </div>
  );
}
